/**
 * ProShip Direct v2 — allocation simulator.
 *
 * Answers the question a merchant actually has when they write a rule: for THIS order, which
 * courier gets it, and was it my rule that decided or somebody else's?
 *
 * THE FAILURE THIS EXISTS TO CATCH is a rule that never fires. Rules are evaluated top to bottom
 * and the first match wins, so a rule written for "COD above 2kg" sitting below a rule for "all
 * COD" is dead on arrival — and nothing on the rules list says so, because both rules are valid,
 * enabled and correct in isolation. The simulator shows every rule it walked past and why each one
 * did not match, which turns an invisible ordering bug into a visible one.
 *
 * Pure functions, no React, so the evaluation is testable independently of the panel.
 *
 * FIDELITY, stated plainly: this mirrors the documented rule semantics — ordered evaluation, first
 * match wins, then the matched rule's strategy ranks the pool. It is NOT the engine. Real
 * allocation additionally checks serviceability, contract limits and live courier health, and PACE
 * scores on features this client cannot see. So a verdict here is "which rule applies", which is
 * exact, plus "which courier that rule would pick", which is indicative. The panel says so.
 */
import { RULE_CONDITION_TYPES } from './defaults';

/** The order attributes a merchant can set up in the simulator, mirroring the condition types. */
export const SIM_FIELDS = RULE_CONDITION_TYPES.map((c) => ({
  key: c.key,
  label: c.label,
  kind: c.kind,
  unit: c.unit,
  options: c.options,
  placeholder: c.placeholder,
}));

export const DEFAULT_SIM_ORDER = {
  paymentMode: 'COD',
  zone: 'METROS',
  // Kilograms now, matching the condition unit — 1200 kg was the giveaway that these had drifted.
  weight: 1.2,
  orderValue: 2400,
  quantity: 1,
  sku: 'NM-CLS-42',
  pincode: '400001',
  channel: 'SHOPIFY',
  state: 'Maharashtra',
  city: 'Mumbai',
  tag: '',
  awbAssignedAt: '18:30',
};

/**
 * "HH:MM" (or an ISO timestamp) to minutes since midnight, or null if it is not a time.
 *
 * Accepts an ISO string because that is what an order actually carries — the rule holds a clock
 * time, the shipment holds a moment, and only the shipment's local hours and minutes matter.
 */
const minutesOfDay = (value) => {
  if (value === undefined || value === null || value === '') return null;

  const clock = String(value).match(/^(\d{1,2}):(\d{2})/);
  if (clock) {
    const h = Number(clock[1]);
    const m = Number(clock[2]);
    return h >= 0 && h < 24 && m >= 0 && m < 60 ? h * 60 + m : null;
  }

  const stamp = new Date(value);
  return Number.isNaN(stamp.getTime()) ? null : stamp.getHours() * 60 + stamp.getMinutes();
};

const asList = (value) =>
  String(value || '')
    .split(/[,|]/)
    .map((v) => v.trim().toLowerCase())
    .filter(Boolean);

const compare = (operator, left, right) => {
  const a = Number(left);
  const b = Number(right);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return false;
  switch (operator) {
    case 'GTE': return a >= b;
    case 'GT': return a > b;
    case 'LTE': return a <= b;
    case 'LT': return a < b;
    case 'EQ': return a === b;
    default: return false;
  }
};

const OPERATOR_TEXT = { GTE: '≥', GT: '>', LTE: '≤', LT: '<', EQ: '=' };

/** How one condition reads, for the "why it did not match" line. */
export function describeCondition(condition) {
  const type = RULE_CONDITION_TYPES.find((c) => c.key === condition.type);
  const label = type?.label || condition.type;

  if (type?.kind === 'compare') {
    return `${label} ${OPERATOR_TEXT[condition.operator] || condition.operator} ${condition.value}${type.unit ? ` ${type.unit}` : ''}`;
  }
  if (type?.kind === 'between') {
    const { from, to } = condition.value || {};
    return `${label} between ${from || '—'} and ${to || '—'}`;
  }
  if (Array.isArray(condition.value)) {
    return `${label} is ${condition.value.join(' or ')}`;
  }
  return `${label} is ${condition.value}`;
}

/** Does one condition hold for this order? */
export function conditionMatches(condition, order) {
  const type = RULE_CONDITION_TYPES.find((c) => c.key === condition.type);
  const actual = order[condition.type];

  if (actual === undefined || actual === null || actual === '') return false;

  switch (type?.kind) {
    case 'compare':
      return compare(condition.operator, actual, condition.value);

    /*
      A clock window, compared in minutes since midnight.

      A window whose end is before its start crosses midnight (22:00 to 06:00), so it matches
      OUTSIDE the numeric range rather than inside it. Getting that wrong is the whole reason this
      is not a pair of compare conditions.

      Both ends are inclusive: a cutoff of 18:00 should catch a shipment stamped exactly 18:00.
    */
    case 'between': {
      const { from, to } = condition.value || {};
      const at = minutesOfDay(actual);
      const start = minutesOfDay(from);
      const end = minutesOfDay(to);
      if (at === null || start === null || end === null) return false;
      return start <= end ? at >= start && at <= end : at >= start || at <= end;
    }

    case 'multi': {
      // A multi condition holds if the order's value is any of the chosen ones.
      const wanted = (Array.isArray(condition.value) ? condition.value : [condition.value]).map((v) =>
        String(v).toLowerCase()
      );
      return wanted.includes(String(actual).toLowerCase());
    }

    case 'list':
    case 'text': {
      // The rule holds a list; the order holds one value. Either side may be comma-separated.
      const wanted = asList(Array.isArray(condition.value) ? condition.value.join(',') : condition.value);
      const mine = asList(actual);
      return mine.some((v) => wanted.includes(v));
    }

    case 'choice':
    default:
      return String(actual).toLowerCase() === String(condition.value).toLowerCase();
  }
}

/**
 * Walks the rules in order and reports every one.
 *
 * Returns a row per rule, not just the winner, because "why did my rule not fire" is the question
 * this is for. `outcome` is one of: MATCHED, SHADOWED (would have matched, but an earlier rule
 * already won), FAILED (a condition did not hold), DISABLED, or CATCH_ALL for a rule with no
 * conditions.
 */
export function evaluateRules(rules, order) {
  let decided = false;
  const rows = [];

  rules.forEach((rule, index) => {
    if (!rule.enabled) {
      rows.push({ rule, index, outcome: 'DISABLED', failed: [] });
      return;
    }

    const failed = (rule.conditions || []).filter((c) => !conditionMatches(c, order));
    const matches = failed.length === 0;
    const catchAll = (rule.conditions || []).length === 0;

    if (!matches) {
      rows.push({ rule, index, outcome: 'FAILED', failed });
      return;
    }
    if (decided) {
      rows.push({ rule, index, outcome: 'SHADOWED', failed: [] });
      return;
    }

    decided = true;
    rows.push({ rule, index, outcome: catchAll ? 'CATCH_ALL' : 'MATCHED', failed: [] });
  });

  return { rows, matched: rows.find((r) => r.outcome === 'MATCHED' || r.outcome === 'CATCH_ALL') || null };
}

/**
 * Ranks the courier pool the way the matched rule's strategy would.
 *
 * Cost and transit are illustrative — derived deterministically from the courier key so the same
 * courier always scores the same, since neither a live quote API nor the merchant's rate card is
 * reachable from here. PACE is ranked on the merchant's own weights where the courier has a
 * scorecard; absent that, it falls back to a blend of the two indicative numbers.
 */
export function rankPool({ strategy, order: ruleOrder = [], pool }) {
  const seedOf = (key) => {
    let h = 0;
    for (let i = 0; i < key.length; i += 1) h = (h * 31 + key.charCodeAt(i)) % 9973;
    return h / 9973;
  };

  const scored = pool.map((courier) => {
    const s = seedOf(courier.key);
    return {
      ...courier,
      cost: Math.round(58 + s * 46),
      transitDays: Number((1.8 + s * 2.6).toFixed(1)),
      paceScore: Number((62 + s * 33).toFixed(1)),
    };
  });

  if (strategy === 'PERSONALIZED') {
    // The merchant's own order, and only the couriers they listed.
    const ranked = ruleOrder
      .map((key) => scored.find((c) => c.key === key))
      .filter(Boolean)
      .map((c, i) => ({ ...c, why: i === 0 ? 'First in your priority list' : `Position ${i + 1} in your list` }));
    return { ranked, basis: 'Your priority order' };
  }
  if (strategy === 'CHEAPEST') {
    return {
      ranked: [...scored]
        .sort((a, b) => a.cost - b.cost)
        .map((c) => ({ ...c, why: `₹${c.cost} landed` })),
      basis: 'Lowest landed cost',
    };
  }
  if (strategy === 'FASTEST') {
    return {
      ranked: [...scored]
        .sort((a, b) => a.transitDays - b.transitDays)
        .map((c) => ({ ...c, why: `${c.transitDays} days transit` })),
      basis: 'Shortest transit',
    };
  }
  return {
    ranked: [...scored]
      .sort((a, b) => b.paceScore - a.paceScore)
      .map((c) => ({ ...c, why: `PACE score ${c.paceScore}` })),
    basis: 'PACE score on your weights',
  };
}

/**
 * The whole simulation: which rule applies, and what it would pick.
 *
 * `winner` is null when no rule matched and there is no catch-all, which is itself the answer — the
 * order would fall to the engine's own default rather than to anything the merchant configured.
 */
export function simulateAllocation({ order, rules, pool }) {
  const { rows, matched } = evaluateRules(rules, order);

  if (!matched) {
    return { rows, matched: null, ranked: [], basis: null, winner: null };
  }

  const { ranked, basis } = rankPool({
    strategy: matched.rule.strategy,
    order: matched.rule.order,
    pool,
  });

  return { rows, matched, ranked, basis, winner: ranked[0] || null };
}
